import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { rootApi, errorText, type BusinessSummary } from "../api";
import { lastBusiness, useBusinesses } from "../business";
import { Banner } from "../editor";
import { Field, SelectField, TextField } from "../fields";
import { BusinessMark, go, useSession } from "../session";
import { Card, Icon, Page } from "../ui";
import { AuthCard } from "./auth";

/** Above any business: the welcome, a new business, an invite. */

/** "/": straight into the business you used last (or your first); the welcome when you're in none yet. */
export function WelcomePage() {
  const { me } = useSession();
  const businesses = useBusinesses();
  const list = businesses.data;
  useEffect(() => {
    if (!list || list.length === 0) return;
    const last = lastBusiness();
    go(`/${list.find((b) => b.handle === last)?.handle ?? list[0]!.handle}`);
  }, [list]);
  if (!me || !list || list.length > 0) return <div className="loading">Loading…</div>;
  const first = me.account.name.split(" ")[0] || "there";
  const weak = !me.security.authenticator && me.security.passkeys === 0;
  return (
    <Page title={`Welcome, ${first}`} sub="You're not part of a business yet. When someone adds you to theirs, it shows up here." narrow>
      {me.account.hq === "owner" && <HqSetup />}
      {me.account.hq && (
        <Card title="Create a business" action={<Link className="btn btn--primary" to="/new">Create a business</Link>}>
          <p className="acct-plain">It gets its own sealed data space, its own key and its own chief of staff.</p>
        </Card>
      )}
      {weak && (
        <Card title="Make your login stronger" action={<Link className="btn" to="/account/security">Security</Link>}>
          <p className="acct-plain">Add a passkey or an authenticator app so a stolen password isn't enough to get in.</p>
        </Card>
      )}
    </Page>
  );
}

function HqSetup() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await rootApi("/businesses/test-set", { method: "POST" });
      await qc.invalidateQueries({ queryKey: ["businesses"] });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };
  return (
    <Card title="Mivio HQ">
      {error && <Banner tone="critical" title="That didn't finish">{error}</Banner>}
      <p className="acct-plain">Mivio HQ (with Alfred) and two test businesses side by side: MyCelsius and a demo run club. Test data only.</p>
      <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void run()}>
        {busy ? "Setting up…" : "Set up Mivio HQ and the test businesses"}
      </button>
    </Card>
  );
}

/** The list of your businesses (from the menu's "All businesses"). */
export function BusinessesCard({ list }: { list: BusinessSummary[] }) {
  return (
    <Card flush>
      <div className="welcome__biz">
        {list.map((b) => (
          <a key={b.handle} href={`/${b.handle}`}>
            <BusinessMark name={b.name} />
            <span>
              <strong>{b.name}</strong>
              <small>{b.role?.name ?? "Member"}</small>
            </span>
            <Icon.chevR />
          </a>
        ))}
      </div>
    </Card>
  );
}

function slug(name: string) {
  return name.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48);
}

export function NewBusinessPage() {
  const { me } = useSession();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [handle, setHandle] = useState<string | null>(null);
  const [currency, setCurrency] = useState(me?.account.currency ?? "AUD");
  const [timeZone, setTimeZone] = useState(me?.account.timeZone ?? "Australia/Sydney");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ field?: string; message: string } | null>(null);
  const zones = useMemo(() => Intl.supportedValuesOf("timeZone").map((z) => ({ value: z, label: z.replace(/_/g, " ") })), []);
  const currencies = useMemo(() => Intl.supportedValuesOf("currency").map((c) => ({ value: c, label: c })), []);
  const address = handle ?? slug(name);
  if (!me?.account.hq) return <Page title="Not found"><Card><p className="acct-plain">There's nothing at this address.</p></Card></Page>;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await rootApi<{ business: BusinessSummary }>("/businesses", { method: "POST", json: { name, currency, timeZone, ...(handle !== null ? { handle } : {}) } });
      await qc.invalidateQueries({ queryKey: ["businesses"] });
      go(`/${r.business.handle}`);
    } catch (err) {
      const field = (err as { details?: { field?: string } }).details?.field;
      setError({ ...(field ? { field } : {}), message: errorText(err) });
      setBusy(false);
    }
  };
  return (
    <Page title="Create a business" sub="It gets its own sealed data space, its own key and its own chief of staff." back="/" narrow>
      <form onSubmit={(e) => void submit(e)}>
        {error && !error.field && <Banner tone="critical" title="The business wasn't created">{error.message}</Banner>}
        <Card>
          <TextField label="Business name" value={name} onChange={setName} maxLength={120} error={error?.field === "name" ? error.message : undefined} help="" />
          <Field label="Address" error={error?.field === "handle" ? error.message : undefined} help={<>{window.location.host}/<strong>{address || "your-business"}</strong></>}>
            {(id, d) => <input id={id} aria-describedby={d} value={address} onChange={(e) => setHandle(e.target.value.toLowerCase())} spellCheck={false} autoCapitalize="none" />}
          </Field>
          <div className="grid-fields">
            <SelectField label="Currency" value={currency} options={currencies} onChange={setCurrency} />
            <SelectField label="Time zone" value={timeZone} options={zones} onChange={setTimeZone} />
          </div>
        </Card>
        <div className="page__foot-actions">
          <button type="submit" className="btn btn--primary" disabled={busy || name.trim().length < 2}>{busy ? "Setting up…" : "Create business"}</button>
        </div>
      </form>
    </Page>
  );
}

interface InviteInfo { business: { name: string }; role: string; email: string; invitedBy: string }

/** /invite/<token>: who invited you, then joining (making a login first if needed). */
export function InvitePage() {
  const { token = "" } = useParams();
  const { me, ready } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["invite", token], queryFn: () => rootApi<InviteInfo>(`/invites/${token}`), retry: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const back = encodeURIComponent(`/invite/${token}`);
  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await rootApi<{ business: BusinessSummary }>(`/invites/${token}/accept`, { method: "POST" });
      await qc.invalidateQueries({ queryKey: ["businesses"] });
      go(`/${r.business.handle}`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };
  if (q.isPending || !ready) return <AuthCard title="Invite"><p className="muted">Checking your invite…</p></AuthCard>;
  if (q.isError || !q.data) return <AuthCard title="This invite doesn't work">{<p className="error">{errorText(q.error)}</p>}</AuthCard>;
  const info = q.data;
  return (
    <AuthCard title={`Join ${info.business.name}`} sub={`${info.invitedBy} invited ${info.email} to join as ${info.role}.`}>
      {error && <p className="error" role="alert">{error}</p>}
      {me ? (
        me.account.email.toLowerCase() === info.email.toLowerCase() ? (
          <button type="button" className="btn btn--accent btn--lg btn--block" disabled={busy} onClick={() => void accept()}>
            {busy ? "Joining…" : `Join ${info.business.name}`}
          </button>
        ) : (
          <Banner tone="info" title={`You're logged in as ${me.account.email}`}>Log out, then log in as {info.email} to accept.</Banner>
        )
      ) : (
        <>
          <Link className="btn btn--accent btn--lg btn--block" to={`/sign-up?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(info.email)}&return=${back}`}>
            Create your Mivio login
          </Link>
          <Link className="btn btn--lg btn--block signin__second" to={`/sign-in?return=${back}`}>
            I already have a Mivio login
          </Link>
        </>
      )}
    </AuthCard>
  );
}
